import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {PreviousRouteService} from '../../../../services/previous-route.service';
import {JsonEditorService} from '../../../../services/json-editor.service';
import {JsonEditorComponent, JsonEditorOptions} from 'ang-jsoneditor';
import {CommonMessagesService} from '../../../../services/common-messages.service';
import {SequenceConfiguration} from '../../../../model/sequence-configuration';

@Component({
  selector: 'app-sequence-form-configuration',
  templateUrl: './sequence-form-configuration.component.html',
  styleUrls: ['./sequence-form-configuration.component.scss']
})
export class SequenceFormConfigurationComponent implements OnInit, AfterViewInit {
  @Input() mode: 'create' | 'edit';
  @Input() sequenceConfiguration: SequenceConfiguration;
  @Input() assetSchemaJson: any;
  @Input() errors: any[];

  @Output() sequenceConfigurationChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() prevStep: EventEmitter<void> = new EventEmitter<void>();
  @Output() nextStep: EventEmitter<void> = new EventEmitter<void>();
  @Output() onCancel: EventEmitter<string> = new EventEmitter<string>();
  @Output() showErrors: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(JsonEditorComponent)
  sequenceConfigurationEditor: JsonEditorComponent;

  configurationForm: FormGroup;

  loaded = false;

  isValid: boolean;

  template: any;

  jsonEditorOptions: JsonEditorOptions;

  constructor(
    public jsonEditorService: JsonEditorService,
    private readonly formBuilder: FormBuilder,
    private readonly previousService: PreviousRouteService,
    private readonly commonMessagesService: CommonMessagesService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.jsonEditorOptions = this.jsonEditorService.buildFormEditorOptions();

    this.configurationForm = this.formBuilder.group({
      configuration: this.sequenceConfiguration ?? undefined
    });

    if (!this.configurationForm.get('configuration').value) {
      this.configurationForm.get('configuration').setValue(this.assetSchemaJson.template);
      this.configurationForm.get('configuration').updateValueAndValidity();
    }
  }

  ngAfterViewInit(): void {
    setTimeout((): void => {
      this.loaded = true;
      this.cdr.detectChanges();
      this.sequenceConfigurationEditor.setSchema(this.assetSchemaJson.schema, {});
      this.isValidSchema();
    });
  }

  onFileChange(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (input.files && input.files.length > 1) {
      this.commonMessagesService.showErrorUploadMultipleFilesWhenUniqueFileMessage();
    } else {
      const file: File = input.files.item(0);
      if (file.type !== 'application/json') {
        this.commonMessagesService.showErrorUploadSequenceConfigurationMessage();
      } else {
        const reader: FileReader = new FileReader();
        reader.onload = (content: ProgressEvent<FileReader>): void => {
          let json: any;
          if (typeof content.target.result === 'string') {
            json = JSON.parse(content.target.result);
          } else {
            json = JSON.parse(new TextDecoder().decode(content.target.result));
          }
          this.configurationForm.get('configuration').setValue(json);
          this.configurationForm.get('configuration').updateValueAndValidity();

          this.isValidSchema().finally(() => {
            this.commonMessagesService.showSuccessfullyUploadedMessage();
          });
        };
        reader.readAsArrayBuffer(file);
      }
    }
    input.value = '';
  }

  onNextStep(): void {
    this.sequenceConfigurationChange.emit(this.sequenceConfigurationEditor.get());
    this.nextStep.emit();
  }

  onPrevStep(): void {
    this.sequenceConfigurationChange.emit(this.sequenceConfigurationEditor.get());
    this.prevStep.emit();
  }

  async isValidSchema(): Promise<void> {
    const errors: any[] = await this.sequenceConfigurationEditor.getEditor().validate();
    this.isValid = errors.length === 0 && this.sequenceConfigurationEditor.isValidJson();
  }

  reloadTemplate(): void {
    this.configurationForm.get('configuration').setValue(this.assetSchemaJson.template);
    this.configurationForm.get('configuration').updateValueAndValidity();
    this.isValidSchema().finally((): void => {
      this.commonMessagesService.showRestoreConfigurationMessage();
    });
  }

  cancel(): void {
    this.onCancel.emit(this.previousService.getPreviousUrl());
  }

}
